/**
 * OpenAI Integration — deterministic request building.
 *
 * Turns the approved inputs into the exact messages the provider receives. The
 * same inputs always produce byte-identical messages: no timestamps, no
 * randomness, no iteration over unordered structures.
 *
 * The Creative Plan is presented as DIRECTIVES that are already decided. The
 * model is never asked to think, choose, or reconsider.
 */

import { PLAN_DECISION_KEYS } from '../thinking';
import {
  PROMPT_SCHEMA_ID, PROMPT_VERSION, PROVIDERS,
  RESPONSE_FORMAT, DEFAULT_TRANSPORT, MAX_COUNT,
} from './openaiSchema';
import { DESIGNER_SYSTEM_PROMPT } from './designerSystemPrompt';
import { buildResponseSchema } from './responseSchema';
import { ICON_NAMES } from '../renderer';
import { EFFECT_KINDS, FILL_KINDS, MASK_KINDS } from '../schema';
import { expectationFor } from '../artDirector';

const asObject = (v) => (v && typeof v === 'object' && !Array.isArray(v) ? v : {});
const asArray = (v) => (Array.isArray(v) ? v : []);
const stable = (v) => JSON.stringify(v, null, 2);

/** Human-readable label for a decision key: "ctaStrategy" → "CTA STRATEGY". */
const label = (key) => key.replace(/([A-Z])/g, ' $1').replace(/\bCta\b/i, 'CTA').toUpperCase();

/** The plan, rendered as decisions already taken — each with the reason behind it. */
const renderPlan = (plan) => PLAN_DECISION_KEYS
  .filter((key) => plan[key] && plan[key].choice !== undefined && plan[key].choice !== null)
  .map((key) => {
    const { choice, why } = plan[key];
    const value = Array.isArray(choice) ? choice.join(' → ') : String(choice);
    return `- ${label(key)}: ${value}\n  WHY: ${why}`;
  })
  .join('\n');

/** The genome, rendered as the language to speak — not a menu to choose from. */
const renderGenome = (genome) => {
  const skip = ['id', 'name', 'expansion'];
  const dims = Object.keys(genome)
    .filter((k) => !skip.includes(k))
    .map((k) => `- ${k}: ${Array.isArray(genome[k]) ? genome[k].join(', ') : genome[k]}`)
    .join('\n');
  return `${genome.name} (${genome.id})\n${dims}\n\nEXPANSION SEEDS (translate these into concrete tokens):\n${stable(genome.expansion || {})}`;
};

/** The research, as background context only — it explains the plan, it does not reopen it. */
const renderResearch = (research) => {
  const keys = Object.keys(research).filter((k) => !['schema', 'version', 'request', 'sources', 'confidence', 'freshness'].includes(k));
  return keys
    .filter((k) => research[k] && (!Array.isArray(research[k]) || research[k].length))
    .map((k) => `- ${k}: ${Array.isArray(research[k]) ? research[k].join(', ') : research[k]}`)
    .join('\n');
};

/** The selected patterns, as the structural knowledge to build from. */
const renderPatterns = (patterns) => patterns.map((p) => [
  `### ${p.name} (${p.id}) — family: ${p.family}`,
  `Purpose: ${p.purpose}`,
  `Composition: ${p.compositionPhilosophy}`,
  `Hierarchy: ${p.hierarchyPhilosophy}`,
  `Typical sections: ${asArray(p.typicalSections).join(', ')}`,
  `Preferred components: ${asArray(p.preferredComponents).join(', ')}`,
  `Premium cues: ${asArray(p.premiumCues).join(', ')}`,
  `Avoid: ${asArray(p.thingsToAvoid).join(', ')}`,
  `Expansion rules: ${stable(p.expansionRules || {})}`,
].join('\n')).join('\n\n');

/**
 * Design Memory's constraints, rendered as hard requirements.
 *
 * These are what stop the tenth flyer for the same industry from being the
 * ninth one again. `avoid` names concrete choices already used by a near
 * neighbour; `recommendedVariationAxes` names the dimensions along which this
 * design must differ. Both are derived deterministically from prior
 * fingerprints — the model never sees the prior designs themselves.
 */
const renderConstraints = (constraints) => {
  const gen = asObject(asObject(constraints).generationConstraints);
  const avoid = asObject(gen.avoid);
  const axes = asArray(gen.recommendedVariationAxes);
  const avoidLines = Object.keys(avoid).sort().map((dim) => {
    const value = avoid[dim];
    return `- ${dim}: do NOT use ${Array.isArray(value) ? value.join(', ') : value}`;
  });

  if (!avoidLines.length && !axes.length) return '';
  return [
    avoidLines.length ? `Already used by a near-identical design — choose differently:\n${avoidLines.join('\n')}` : '',
    axes.length ? `Vary meaningfully along these axes: ${axes.join(', ')}` : '',
  ].filter(Boolean).join('\n\n');
};

/**
 * Concepts already produced in this run, rendered so the next concept is
 * designed AGAINST them rather than beside them.
 */
const renderPriorConcepts = (priorConcepts) => asArray(priorConcepts)
  .map((c, i) => `${i + 1}. ${c.concept || 'untitled'} — composition: ${c.composition || '?'}, hierarchy: ${asArray(c.hierarchy).join(' → ') || '?'}, typography: ${c.typography || '?'}, color: ${c.color || '?'}, decoration: ${c.decoration || '?'}, photography: ${c.photography || '?'}`)
  .join('\n');

/**
 * Build the user message: the approved intent plus the contract to express it in.
 * @returns {string}
 */
/**
 * The brand half of the brief, as prompt lines.
 *
 * These are the operator's own words and assets — a campaign title, a brand
 * name, a palette, the contact details that belong on the piece. They are
 * given to the designer as CONTENT and CONSTRAINT, not as layout instructions:
 * where the logo sits and how the palette is used stay the model's decisions.
 *
 * Anything the operator left blank is omitted rather than sent as an empty
 * line, which would read as "this is deliberately nothing".
 */
export const briefBrandLines = (brief) => {
  const b = asObject(asObject(brief).brand);
  const s = asObject(asObject(brief).settings);
  const lines = [];

  if (b.language) lines.push(`Language: write ALL copy in ${b.language}.`);
  if (b.region) lines.push(`Region: ${b.region} — use idiom, imagery and conventions that read as local there.`);
  if (b.campaignTitle) lines.push(`Campaign title: "${b.campaignTitle}" — this is the message the piece leads with.`);
  if (b.brandName) lines.push(`Brand name: "${b.brandName}" — it must appear, treated as the brand signature.`);
  if (b.logo) lines.push(`Logo: supplied. Place it as the brand mark, at a size and position the composition earns.`);

  const colors = asObject(b.colors);
  if (Object.keys(colors).length) {
    const pairs = Object.entries(colors).map(([slot, hex]) => `${slot} ${hex}`).join(', ');
    lines.push(`Brand colours: ${pairs}. Build the palette from these — they are the brand, not a suggestion.`);
  }

  const contact = asObject(b.contact);
  if (Object.keys(contact).length) {
    const pairs = Object.entries(contact).map(([k, v]) => `${k}: ${v}`).join(' · ');
    lines.push(`Contact details to include: ${pairs}. Set them as supporting information, never competing with the headline.`);
  }

  if (s.images) lines.push(`Imagery preference: ${s.images}.`);
  if (s.variation) lines.push(`Creative variation: ${s.variation}.`);
  if (s.quality) lines.push(`Quality bar: ${s.quality}.`);

  return lines;
};

export const buildUserPrompt = (inputs = {}, count = 1) => {
  const plan = asObject(inputs.plan);
  const genome = asObject(inputs.genome);
  const research = asObject(inputs.research);
  const patterns = asArray(inputs.patterns);
  const meta = asObject(research.request);
  const documentType = meta.documentType || plan.meta?.documentType || '';
  const expect = expectationFor(documentType);
  const constraintText = renderConstraints(inputs.constraints);
  const priorText = renderPriorConcepts(inputs.priorConcepts);
  const corrections = asArray(inputs.corrections);

  return [
    `# BRIEF`,
    `Document type: ${meta.documentType || plan.meta?.documentType || 'as specified in the plan'}`,
    `Industry: ${meta.industry || 'general'}`,
    `Purpose: ${meta.purpose || 'branding'}`,
    `Audience: ${meta.audience || 'general'}`,
    ...briefBrandLines(inputs.brief),
    ``,
    `# APPROVED CREATIVE PLAN — ALREADY DECIDED, TRANSLATE FAITHFULLY`,
    `These decisions were made by the creative director and approved by the art director.`,
    `Execute them exactly. Do not reconsider, substitute, or improve upon them.`,
    ``,
    renderPlan(plan),
    ``,
    `# STYLE GENOME — THE LANGUAGE YOU ARE SPEAKING`,
    renderGenome(genome),
    ``,
    `# SELECTED DESIGN PATTERNS — THE STRUCTURE TO BUILD`,
    renderPatterns(patterns) || 'None selected; follow the composition strategy in the plan.',
    ``,
    `# RESEARCH CONTEXT — BACKGROUND ONLY`,
    `This explains why the plan is what it is. It does not reopen any decision.`,
    renderResearch(research),
    ``,
    ...(constraintText ? [
      `# UNIQUENESS CONSTRAINTS — BINDING`,
      `Design Memory compared this brief against designs already produced.`,
      constraintText,
      ``,
    ] : []),
    ...(priorText ? [
      `# CONCEPTS ALREADY PRODUCED IN THIS RUN — DO NOT REPEAT THEM`,
      `These are the other concepts for this same brief. Yours must differ in`,
      `composition, hierarchy, visual treatment, imagery strategy, typography or`,
      `decorative language — not merely in colour or wording.`,
      priorText,
      ``,
    ] : []),
    ...(corrections.length ? [
      `# CORRECTIONS REQUIRED — YOUR PREVIOUS ATTEMPT WAS REJECTED`,
      ...(corrections.some((c) => c.kind !== 'visual') ? [
        `## Structural faults — fix exactly these and change nothing else. Each names the path that was wrong.`,
        corrections.filter((c) => c.kind !== 'visual')
          .map((c) => `- ${c.path ? `${c.path}: ` : ''}${c.message}${c.actual ? ` (you sent ${c.actual})` : ''}`).join('\n'),
        ``,
      ] : []),
      // A weak composition is not a bad value at a path. Told to "fix exactly
      // this", the model returns the same page with one number changed; the
      // page has to be designed again.
      ...(corrections.some((c) => c.kind === 'visual') ? [
        `## Design faults — the art director MEASURED the rendered page and rejected it.`,
        `These are not one-line fixes. REDESIGN the composition so each is genuinely resolved.`,
        `Keep the campaign message, the copy, the palette and the imagery subject you already chose — that work was accepted. Change the composition, the scale relationships and the hierarchy.`,
        corrections.filter((c) => c.kind === 'visual')
          .map((c) => `- MEASURED: ${c.message}\n  REQUIRED: ${c.request || ''}`).join('\n'),
        ``,
      ] : []),
    ] : []),
    `# DESIGN JSON V2 — THE OUTPUT CONTRACT`,
    `Return exactly ${count} document${count === 1 ? '' : 's'} in { "documents": [ ... ] }.`,
    `Each document must conform to this JSON Schema:`,
    stable(buildResponseSchema(count)),
    ``,
    `# WHAT A ${(documentType || 'DOCUMENT').toUpperCase()} MUST CONTAIN`,
    `This is ${expect.character}.`,
    `It needs at least ${expect.minContentRoles} content-carrying sections and ${expect.minTypeLevels} distinct type levels.`,
    expect.imagery ? `It needs a photographic or framed subject.` : `It does NOT need photography — restraint is correct here.`,
    expect.cta ? `It must ask the viewer to do something.` : `It does NOT need a call-to-action block.`,
    expect.supporting ? `It needs real substance beneath the lead — features, stats, an offer or pricing.` : `It does NOT need supporting content blocks; the lead carries it.`,
    `Aim well past the floor of ${expect.minNodes} nodes where the type warrants it — but never add an element that carries no design intent. Filler is worse than sparseness.`,
    expect.supporting ? `Real parts that carry content, not decoration: an eyebrow or kicker above the headline; a subheadline that earns the claim; ${expect.minContentRoles} or more benefit items that each pair an icon with a LABEL and a line of copy; a proof point or testimonial; the offer with its terms; a footer identity. A page with a headline, three bare labels and a phone number is a wireframe of a flyer, not a flyer.` : ``,
    `Colour is layered, never one flat fill: page.background takes an ARRAY — a gradient ground, then a low-opacity "noise" or "texture" pass — with solid surfaces above it.`,
    `Give the cards and the hero real depth: effects: [{ "kind": "dropShadow", "color": "onSurface", "y": 8, "blur": 24, "opacity": 0.12 }]. Restrained, but present.`,
    ``,
    `## HIERARCHY — MEASURED, NOT SUGGESTED`,
    `The art director measures the rendered page. These are the numbers it checks:`,
    `- ONE line is the headline, set at display scale: its type size must be at least ${Math.round(expect.hierarchy.display * 100)}% of the canvas width (${Math.round(expect.hierarchy.display * 1080)}px on a 1080px-wide canvas). Give it the "display" type token.`,
    `- That headline node must ALSO carry role "headline". Being the biggest line by accident is not a hierarchy: the role is what states the decision, and it is checked. Give contact, phone, address and footer nodes their own roles ("contact", "footer") so they read as secondary.`,
    `- The headline must be at least ${expect.hierarchy.dominance}x more PROMINENT than the next text. Prominence blends size, weight, width and position, so size alone is not the whole of it — which is why the safe way to satisfy this is a real gap in the type scale: give "display" a step at least 4 above "h1", so the rendered headline is roughly twice the size of the line beneath it. Steps of 6 and 5 render 20px apart and read as one flat rank.`,
    `- The subheadline, offer line and section headings all sit BELOW the headline in that scale. If two lines look equally important, the hierarchy has failed however the tokens are named.`,
    `- Contact, footer, phone, email, address and legal text must stay at least ${expect.hierarchy.overMetadata}x BELOW the headline. Small print that competes with the message is the single most common failure.`,
    `- Step the scale deliberately: display → h1/h2 → body → caption. Do not set everything within a few pixels of each other.`,
    `- In a type token, "leading" is a line-height MULTIPLIER (1.0–1.6), never a pixel value. "leading": 48 on a display face makes one headline thousands of pixels tall and pushes the rest of the design off the bottom of the page. "tracking" is likewise relative, and "step" is a position on the scale, not a size in px.`,
    ``,
    `## COMPOSITION BALANCE — MEASURED`,
    `- Content must cover at least ${Math.round(expect.balance.minCoverage * 100)}% of the page on average across its full height.`,
    `- No more than ${Math.round(expect.balance.maxEmptyRun * 100)}% of the page may sit empty in one unbroken run. A dead lower third is the failure this catches.`,
    `- Whitespace is welcome when it frames a strong focal mass. It is not welcome as leftover space.`,
    `- One dominant visual mass — a framed photograph, a full-bleed band, or type set at real scale — must cover at least ${Math.round(expect.focal.mass * 100)}% of the page and anchor the composition.`,
    expect.imagery ? `- If you declare a HERO image, that image itself must cover at least ${Math.round(expect.focal.hero * 100)}% of the canvas. A hero at 4% is a thumbnail beside the text, and it is rejected. A large button elsewhere does not make up for it.` : ``,
    expect.cta ? `- The call to action must be findable but SUBORDINATE: keep the action block under ${Math.round(expect.focal.ctaMax * 100)}% of the canvas, and never larger than the focal mass. A button that outweighs the hero inverts the whole page.` : ``,
    ``,
    `HOW to satisfy the two rules above — these are the schema mechanisms, not style advice:`,
    `- Exactly ONE section must set autoLayout.sizing to "fill". Sections size to their content by default, so a page whose sections all hug leaves the leftover height empty at the BOTTOM. The "fill" section absorbs it. Choose the section that deserves the room — usually the hero.`,
    expect.imagery ? `- The focal image node must ALSO set sizing "fill", and its section must use a "stack" autoLayout. Height only propagates down a stack: in a "grid" or "row" the image keeps its small intrinsic height, so the section grows and the image does not, leaving a hole above and below it. That is the hero-size rule failing on a page that otherwise looks full.` : ``,
    ``,
    `# TASK`,
    `Build ${count === 1 ? 'a' : count} complete, premium, marketplace-quality Design JSON V2 document${count === 1 ? '' : 's'} that realise${count === 1 ? 's' : ''} the approved plan above.`,
    count > 1 ? `Each concept must express the SAME approved plan through a DIFFERENT composition — vary structure, not strategy.` : ``,
    `Use real tokens, layered backgrounds, semantic sections, components, groups, decorations, effects, masks, constraints and editability.`,
    `The canvas is in PIXELS, never millimetres or points: at least 600 wide, and 1080x1350 is a good flyer. 210x297 is A4 in mm and renders as a thumbnail.`,
    ``,
    `## IMAGERY — YOU SPECIFY IT, YOU DO NOT SUPPLY IT`,
    `Never write an image "src". Not a URL, not "path/to/photo.jpg", not "https://example.com/hero.jpg", not a {{IMAGE1}} token. You cannot know a real image URL, and every one you invent renders as a broken image.`,
    `Instead, every image node carries an "assetIntent" describing the photograph you want. A stock library resolves it to a real picture. Say what the picture must SHOW; the search is literal, so use everyday words a photographer would tag, not marketing language:`,
    `  { "type": "image", "role": "hero", "assetIntent": {`,
    `      "subject": "modern family dental clinic",`,
    `      "searchQuery": "modern family dental clinic",`,
    `      "keywords": ["dentist", "family", "modern", "clean", "healthcare"],`,
    `      "orientation": "landscape",       // landscape | portrait | square`,
    `      "mood": ["premium", "clean", "welcoming"],`,
    `      "cropStrategy": "wide",`,
    `      "focalPoint": { "x": 0.5, "y": 0.4 },`,
    `      "required": true,`,
    `      "sourcePreference": "pixabay"`,
    `  } }`,
    `- "searchQuery" is what actually gets searched. Keep it to a findable subject: "dentist examining child" resolves; "the warmth of trusted family care" resolves to nothing.`,
    `- "keywords" are the fallback. If the full query finds nothing, they are searched instead, so make each one a real, common noun.`,
    `- Mark "required": true for any image the composition cannot work without. A required image that resolves to nothing fails the design outright.`,
    `- "orientation" must match the space you gave the node: a portrait photo in a wide hero band will be cropped to nothing.`,
    `Effects, fills and masks have FIXED vocabularies. Inventing a name — "shadow-subtle" for a drop shadow, say — fails validation outright:`,
    `  effects: ${EFFECT_KINDS.join(', ')}`,
    `  fills:   ${FILL_KINDS.join(', ')}`,
    `  masks:   ${MASK_KINDS.join(', ')}`,
    `Icons are NAMED, and the rendering engine knows exactly these names:`,
    `  ${[...ICON_NAMES].sort().join(', ')}`,
    `Use ONLY those. Any other name renders as a neutral accent disc rather than the icon you meant, and a character (emoji, ⬢, ★) is not iconography at all. If none fits, design a shape treatment instead of asking for an icon.`,
    `Express composition structurally through sections, auto-layout, sizing and constraints. Do not emit x, y, width or height — the rendering engine resolves all geometry.`,
    `Return JSON only.`,
  ].filter((line) => line !== null).join('\n');
};

/**
 * Build the complete provider request.
 *
 * @param {Object} inputs
 * @param {Object} inputs.plan       the APPROVED Creative Plan.
 * @param {Object} inputs.research   Research Brief.
 * @param {Object} inputs.genome     Style Genome.
 * @param {Object[]} inputs.patterns selected Library Patterns.
 * @param {Object} [inputs.constraints]    Design Memory generation constraints.
 * @param {Object[]} [inputs.priorConcepts] fingerprints of concepts already produced this run.
 * @param {Object[]} [inputs.corrections]  validation findings from a rejected attempt.
 * @param {Object} [options] { count, provider, maxTokens }. The model is not
 *   among them: the server selects it from its own configuration.
 * @returns {Object} the request the provider transports verbatim.
 */
export const buildDesignRequest = (inputs = {}, options = {}) => {
  const opts = asObject(options);
  const provider = PROVIDERS.includes(opts.provider) ? opts.provider : 'openai';
  const count = Math.max(1, Math.min(Number(opts.count) || 1, MAX_COUNT));

  return {
    schema: PROMPT_SCHEMA_ID,
    version: PROMPT_VERSION,
    provider,
    count,
    max_tokens: Math.max(256, Number(opts.maxTokens) || DEFAULT_TRANSPORT.maxTokens),
    response_format: RESPONSE_FORMAT,
    response_schema: buildResponseSchema(count),
    messages: [
      { role: 'system', content: DESIGNER_SYSTEM_PROMPT },
      { role: 'user', content: buildUserPrompt(inputs, count) },
    ],
  };
};
